'use client';

import { useFrame } from '@react-three/fiber';
import type { FC } from 'react';
import { useRef } from 'react';
import { spring, spring3 } from 'math/time';
import { MathUtils, PerspectiveCamera } from 'three';
import { type BezierPoints, skyState } from '../skyStore';

const TRAFFIC_LAYER = 1;
const MIN_SMOOTH_TIME = 0.05;

const evalBezier = (p: BezierPoints, x: number): number => {
  const [x1, y1, x2, y2] = p;
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  let t = x;
  for (let i = 0; i < 8; i++) {
    const xt = ((ax * t + bx) * t + cx) * t - x;
    const d = (3 * ax * t + 2 * bx) * t + cx;
    if (Math.abs(xt) < 1e-5 || d === 0) break;
    t -= xt / d;
  }
  t = MathUtils.clamp(t, 0, 1);
  return ((ay * t + by) * t + cy) * t;
};

export interface CameraFrame {
  pitch: number;
  yaw: number;
  height: number;
  fov: number;
}

export const SkyCamera: FC<{ onCamera?: (c: CameraFrame) => void }> = ({
  onCamera,
}) => {
  const position = useRef(spring3.create([0, skyState.camera.heightTop, 0]));
  const pitch = useRef(
    spring.create((skyState.camera.pitchTop * Math.PI) / 180)
  );
  const yaw = useRef(spring.create(0));
  const fov = useRef(spring.create(skyState.camera.fovTop));

  useFrame(({ camera, clock }, delta) => {
    const t = clock.elapsedTime;
    const cam = skyState.camera;
    const p = skyState.scroll;

    const pitchProgress = evalBezier(cam.pitchBezier, p);
    const heightProgress = evalBezier(cam.heightBezier, p);
    const fovProgress = evalBezier(cam.fovBezier, p);
    const sway = cam.sway + p * (cam.swayBottom - cam.sway);

    const targetPitch =
      ((cam.pitchTop + pitchProgress * (cam.pitchBottom - cam.pitchTop)) *
        Math.PI) /
        180 +
      Math.sin(t * 0.35 + Math.PI / 2) * 0.012 * sway;
    const targetYaw =
      (skyState.yaw * Math.PI) / 180 + Math.sin(t * 0.35) * 0.02 * sway;
    const targetHeight =
      cam.heightTop + heightProgress * (cam.heightBottom - cam.heightTop);
    const targetFov = cam.fovTop + fovProgress * (cam.fovBottom - cam.fovTop);

    spring3.damp(
      position.current,
      [0, targetHeight, 0],
      Math.max(MIN_SMOOTH_TIME, 1 / cam.followSpeed),
      delta
    );
    spring.dampAngle(
      pitch.current,
      targetPitch,
      Math.max(MIN_SMOOTH_TIME, 1 / cam.pitchFollow),
      delta
    );
    spring.damp(
      yaw.current,
      targetYaw,
      Math.max(MIN_SMOOTH_TIME, 1 / cam.followSpeed),
      delta
    );
    spring.damp(
      fov.current,
      targetFov,
      Math.max(MIN_SMOOTH_TIME, 1 / cam.followSpeed),
      delta
    );

    camera.layers.enable(TRAFFIC_LAYER);
    camera.position.set(...position.current.value);
    camera.rotation.order = 'YXZ';
    camera.rotation.y = yaw.current.value;
    camera.rotation.x = pitch.current.value;

    if (
      camera instanceof PerspectiveCamera &&
      Math.abs(camera.fov - fov.current.value) > 0.01
    ) {
      camera.fov = fov.current.value;
      camera.updateProjectionMatrix();
    }

    onCamera?.({
      pitch: camera.rotation.x,
      yaw: camera.rotation.y,
      height: camera.position.y,
      fov: fov.current.value,
    });
  });
  return null;
};
